import {Card, DataStatus} from 'platform/components';
import {Box, Center, Grid, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

import {useGetArticleStatisticsQuery} from '@dms/api/reporting';
import {useChartColors} from '@dms/features/report-charts';
import i18n from '@dms/i18n';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {getIssueDomain} from './getIssueDomain';
import {StatisticsAbcXyz} from './StatisticsAbcXyz';
import {StatisticsAging} from './StatisticsAging';
import {StatisticsChannels} from './StatisticsChannels';
import {StatisticsKpis} from './StatisticsKpis';

const CHART_HEIGHT = 28;
const CLOSED_MONTHS_PER_YEAR = 12;
const AXIS_WIDTH = 60;
const CHART_COLORS = {primary: 'palettes.blue.60.100'} as const;

interface Month {
  month: string;
  netIssuedQuantity: number | null;
  endPurchaseValue: number | null;
}

interface IssueYearChartProps {
  months: Month[];
  unit: string;
  domain: [number, number];
}

function IssueYearChart(props: IssueYearChartProps) {
  const formatNumber = useFormatNumber();
  const {primary} = useChartColors(CHART_COLORS);
  return (
    <Box width="100%" height={CHART_HEIGHT}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={props.months} accessibilityLayer>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="month" axisLine={false} tickLine={false} />
          <YAxis
            domain={props.domain}
            width={AXIS_WIDTH}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => formatNumber(value, 0) ?? ''}
          />
          <Tooltip
            formatter={(value: number) =>
              `${formatNumber(value, 2) ?? EMPTY_PLACEHOLDER} ${props.unit}`
            }
          />
          <Bar
            dataKey="netIssuedQuantity"
            name={i18n.t('entity.warehouse.reporting.monthlyIssues')}
            fill={primary}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

interface ValueChartProps {
  months: Month[];
  currency: string;
}

function ValueChart(props: ValueChartProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const {primary} = useChartColors(CHART_COLORS);
  return (
    <Box width="100%" height={CHART_HEIGHT}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={props.months} accessibilityLayer>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="month" axisLine={false} tickLine={false} />
          <YAxis
            width={AXIS_WIDTH}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => formatNumber(value, 0) ?? ''}
          />
          <Tooltip
            formatter={(value: number) =>
              formatCurrency(value, props.currency, 2) ?? EMPTY_PLACEHOLDER
            }
          />
          <Bar
            dataKey="endPurchaseValue"
            name={i18n.t('entity.warehouse.reporting.purchaseValue')}
            fill={primary}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

interface StatisticsHistoryProps {
  currency: string;
  handlingUnitLabel: string;
  currentInventoryQuantity?: number;
  currentInventoryValue?: number;
  oldestStockAge?: number;
  oldestStockDate?: string;
}

export function StatisticsHistory(props: StatisticsHistoryProps) {
  const {warehouseId, articleId} = useWarehouseParams();
  const formatDate = useDateTimeFormatter();
  const {data, isLoading, isFetching, isError, refetch} = useGetArticleStatisticsQuery({
    warehouseId,
    articleId,
  });

  if (!data) {
    return (
      <VStack spacing={3}>
        <StatisticsKpis
          months={[]}
          averageInventoryQuantity={null}
          currentInventoryQuantity={props.currentInventoryQuantity}
          currentInventoryValue={props.currentInventoryValue}
          oldestStockAge={props.oldestStockAge}
          oldestStockDate={props.oldestStockDate}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
        <Center width="100%">
          <DataStatus
            isLoading={isLoading || isFetching}
            isError={isError && !isFetching}
            errorMessage={i18n.t('feature.customerInsights.sectionError')}
            errorAction={{title: i18n.t('general.actions.retry'), onClick: () => void refetch()}}
          />
        </Center>
      </VStack>
    );
  }
  const oldestTwelve = data.months.slice(0, CLOSED_MONTHS_PER_YEAR);
  const latestTwelve = data.months.slice(CLOSED_MONTHS_PER_YEAR);
  const issueDomain = getIssueDomain(data.months);
  const hasIssueHistory = data.months.some(
    (month) => (month.issuedQuantity ?? 0) > 0 || (month.returnedQuantity ?? 0) > 0
  );
  const hasValueHistory = latestTwelve.some((month) => month.endPurchaseValue !== null);
  const historicalAsOf = formatDate('dateShort', parseDate(data.asOf));
  const historicalBasis = `${i18n.t('entity.warehouse.reporting.snapshotDate')}: ${historicalAsOf}`;
  return (
    <VStack spacing={4}>
      <StatisticsKpis
        months={data.months}
        averageInventoryQuantity={data.averageInventoryQuantity}
        historicalBasis={historicalBasis}
        currentInventoryQuantity={props.currentInventoryQuantity}
        currentInventoryValue={props.currentInventoryValue}
        oldestStockAge={props.oldestStockAge}
        oldestStockDate={props.oldestStockDate}
        currency={props.currency}
        handlingUnitLabel={props.handlingUnitLabel}
      />
      <Grid columns={[1, 2]} spacing={3} align="flex-start">
        <Card title={i18n.t('entity.warehouse.reporting.monthlyIssues')}>
          <VStack spacing={2}>
            <Text color="secondary">{historicalBasis}</Text>
            {hasIssueHistory ? (
              <>
                <IssueYearChart
                  months={oldestTwelve}
                  unit={props.handlingUnitLabel}
                  domain={issueDomain}
                />
                <IssueYearChart
                  months={latestTwelve}
                  unit={props.handlingUnitLabel}
                  domain={issueDomain}
                />
              </>
            ) : (
              <Text>{i18n.t('entity.warehouse.reporting.noIssueHistory')}</Text>
            )}
          </VStack>
        </Card>
        <StatisticsAbcXyz
          classification={data.abcXyz}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
      </Grid>
      <Grid columns={[1, 2]} spacing={3} align="flex-start">
        <Card title={i18n.t('page.warehouse.labels.monthlyInventoryClosing')}>
          <VStack spacing={2}>
            <Text color="secondary">{historicalBasis}</Text>
            <Text>{i18n.t('entity.warehouse.reporting.purchaseValue')}</Text>
            {hasValueHistory ? (
              <ValueChart months={latestTwelve} currency={props.currency} />
            ) : (
              <Text>{i18n.t('entity.warehouse.reporting.inventoryTurnoverIncompleteSeries')}</Text>
            )}
          </VStack>
        </Card>
        <StatisticsAging
          buckets={data.stockAging}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
      </Grid>
      <StatisticsChannels
        channels={data.issueChannels}
        historicalBasis={historicalBasis}
        handlingUnitLabel={props.handlingUnitLabel}
      />
    </VStack>
  );
}
